<template>
  <div class="login-page">
    <h1>Einloggen</h1>
    <h3>Bitte melde dich mit deiner E-Mail oder deinem Schlüsselloch an.</h3>
    
    <form @submit.prevent="handleSubmit" class="login-form">
      <div class="error-message" v-if="error">{{ error }}</div>
      <div class="success-message" v-if="successMessage">{{ successMessage }}</div>
      
      <div class="input-group">
        <input
          class="textInput"
          v-model="email"
          type="email"
          placeholder="E-Mail"
        />
      </div>
      
      <div 
        class="keyhole-drop-zone" 
        :class="{ 'is-dragging': isDragging }"
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @dragover.prevent
        @drop.prevent="handleKeyholeUpload"
        @click="triggerFileInput"
      >
        <input 
          type="file" 
          ref="fileInput" 
          style="display: none" 
          accept=".svg"
          @change="handleFileSelect"
        />
        <img src="/assets/svgs/upload.svg" alt="Upload" class="menu-button"/>

        <br>
        <h3>Ziehe dein Schlüsselloch hier rein.</h3>
        <h3 class="small">(oder klicke zum Auswählen)</h3>
       
      </div>


      <div class="register-link">
        <h4>
          Du hast noch kein Schlüsselloch?
          <router-link to="/registrieren">Jetzt Registrieren</router-link>
        </h4>
      </div>
            
      <button type="submit" :disabled="isLoading || !email">
        {{ isLoading ? 'Anmelden...' : buttonText }}
      </button>
    </form>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { loginWithEmail, loginWithKeyhole, loginWithToken } from '../services/auth'

export default {
  name: 'LoginPage',
  props: {
    token: {
      type: String,
      default: null
    }
  },
  
  setup(props) {
    const router = useRouter()
    const route = useRoute()
    const email = ref('')
    const error = ref('')
    const successMessage = ref('')
    const isLoading = ref(false)
    const isDragging = ref(false)
    const fileInput = ref(null)
    const buttonText = ref('Jetzt Anmelden')
    
    // Handle token login when component is mounted
    onMounted(async () => {
      // Check if we have a token from props or query parameters
      const tokenFromRoute = props.token || route.query.token
      
      if (tokenFromRoute) {
        await handleTokenLogin(tokenFromRoute)
      }
    })
    
    const handleTokenLogin = async (token) => {
      try {
        isLoading.value = true
        error.value = ''
        successMessage.value = 'Token wird überprüft...'
        
        const response = await loginWithToken(token)
        
        if (response.success) {
          // Set login method cookie
          document.cookie = `login_method=email; path=/; max-age=2592000`
          
          // Set auth token
          document.cookie = `auth_token=${response.token}; path=/; max-age=2592000`
          
          successMessage.value = 'Login erfolgreich! Du wirst weitergeleitet...'
          
          // Redirect to account page after short delay
          setTimeout(() => {
            router.push('/accountpage')
          }, 1000)
        }
      } catch (e) {
        error.value = e.message || 'Es gab einen Fehler'
        successMessage.value = ''
      } finally {
        isLoading.value = false
      }
    }
    
    const handleSubmit = async () => {
      if (!email.value) return
      
      try {
        isLoading.value = true
        error.value = ''
        const response = await loginWithEmail(email.value)
        
        if (response.success) {
          // Set login method cookie
          document.cookie = `login_method=email; path=/; max-age=2592000`
          
          // Set auth token if provided
          if (response.token) {
            document.cookie = `auth_token=${response.token}; path=/; max-age=2592000`
          }
          
          // Just change the button text to indicate email was sent
          buttonText.value = 'E-Mail wurde gesendet'
          successMessage.value = response.message
          isLoading.value = false
        }
      } catch (e) {
        error.value = e.message || 'Ein Fehler ist aufgetreten'
      } finally {
        isLoading.value = false
      }
    }
    
    const triggerFileInput = () => {
      fileInput.value.click()
    }

    const handleFileSelect = async (event) => {
      const file = event.target.files[0]
      if (file) {
        await processKeyholeFile(file)
      }
    }
    
    const handleKeyholeUpload = async (event) => {
      isDragging.value = false
      const file = event.dataTransfer.files[0]
      if (file) {
        await processKeyholeFile(file)
      }
    }

    const processKeyholeFile = async (file) => {
      if (!file.name.toLowerCase().endsWith('.svg')) {
        error.value = 'Bitte wähle eine SVG-Datei aus'
        return
      }

      try {
        isLoading.value = true
        error.value = ''
        
        // Read the file content to store it
        const reader = new FileReader()
        reader.onload = async (e) => {
          const svgContent = e.target.result
          
          // Store the SVG content in localStorage
          localStorage.setItem('auth_keyhole', svgContent)
          
          const response = await loginWithKeyhole(file)
          if (response.success) {
            // Mark this as a keyhole login
            document.cookie = `login_method=keyhole; path=/; max-age=2592000`
            document.cookie = `login_keyhole=true; path=/; max-age=2592000`
            // Set auth token
            document.cookie = `auth_token=${response.token}; path=/; max-age=2592000`
            
            // Add a data attribute to the SVG to identify it as a login keyhole
            const processedSvg = svgContent.replace(/<svg/, '<svg data-keyhole-login="true"')
            localStorage.setItem('auth_keyhole', processedSvg)
            
            const redirect = route.query.redirect || '/accountpage'
            router.push(redirect)
          }
        }
        reader.readAsText(file)
      } catch (e) {
        error.value = e.message || 'Login fehlgeschlagen'
      } finally {
        isLoading.value = false
      }
    }
    
    return {
      email,
      error,
      successMessage,
      isLoading,
      isDragging,
      fileInput,
      buttonText,
      handleSubmit,
      handleKeyholeUpload,
      handleFileSelect,
      triggerFileInput
    }
  }
}
</script>

<style scoped>
.processing-message {
  text-align: center;
  margin: 2rem 0;
}

.success-message {
  color: green;
  margin-bottom: 1rem;
}

.error-message {
  color: red;
  margin-bottom: 1rem;
}

/* Add your existing styles here */
</style>